/**
 * Hands the RDP password from the Desktops page to the desktop-session tab it
 * opens, without putting it in a URL (F11).
 *
 * The password used to travel as ?password= on the new tab's URL, which wrote it
 * into browser history, session restore and any proxy/access log. Now:
 *
 *   1. The opener keeps the password in memory only, under a random one-time
 *      nonce, and opens /desktop-session/:id?handoff=<nonce>.
 *   2. The new tab posts { ready, nonce } to window.opener.
 *   3. The opener answers that exact window (event.source must be the tab it
 *      opened, same origin) with the password, then forgets it.
 *
 * Why postMessage rather than sessionStorage/localStorage: storage is written to
 * disk (localStorage always; sessionStorage for session restore), and a copied
 * sessionStorage lingers in the new tab until something deletes it. Here the
 * password only ever lives in the two tabs' JS heaps. The nonce in the URL is
 * worthless on its own: it is single-use, expires after HANDOFF_TTL_MS, and is only
 * honoured for the window the opener itself created.
 *
 * Requires window.open WITHOUT noopener (the session tab needs window.opener) and
 * no Cross-Origin-Opener-Policy that severs same-origin openers (nginx.conf sets
 * none today). If the opener is gone, takeRdpPassword resolves null and the session
 * connects without a password.
 */

const READY = 'd2d-rdp-handoff:ready';
const SECRET = 'd2d-rdp-handoff:secret';

/** How long the opener holds a password waiting for its tab to ask for it. */
export const HANDOFF_TTL_MS = 60_000;

interface Pending {
    secret: string;
    target: Window | null;
    timer: ReturnType<typeof setTimeout>;
}

const pending = new Map<string, Pending>();
let listening = false;

/** 128 random bits, hex. */
export function createNonce(): string {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
}

function onOpenerMessage(event: MessageEvent) {
    if (event.origin !== window.location.origin) return;
    const data = event.data as { type?: string; nonce?: string } | null;
    if (!data || data.type !== READY || typeof data.nonce !== 'string') return;
    const entry = pending.get(data.nonce);
    if (!entry || !entry.target || event.source !== entry.target) return;
    // Single use: forget it before answering.
    pending.delete(data.nonce);
    clearTimeout(entry.timer);
    entry.target.postMessage({ type: SECRET, nonce: data.nonce, secret: entry.secret }, window.location.origin);
}

/**
 * Opener side. Holds `secret` for the window `target` until it asks once, or for
 * HANDOFF_TTL_MS. Call right after window.open returns.
 */
export function offerRdpPassword(nonce: string, secret: string, target: Window | null): void {
    if (!target) return;
    if (!listening) {
        window.addEventListener('message', onOpenerMessage);
        listening = true;
    }
    const timer = setTimeout(() => pending.delete(nonce), HANDOFF_TTL_MS);
    pending.set(nonce, { secret, target, timer });
}

/** Test hook: how many passwords the opener is still holding. */
export function pendingHandoffCount(): number {
    return pending.size;
}

/**
 * Session-tab side. Asks window.opener for the password filed under `nonce`.
 * Resolves the password, or null when there is no opener or no answer in time.
 */
export function takeRdpPassword(nonce: string, timeoutMs = 5_000): Promise<string | null> {
    const opener = window.opener as Window | null;
    if (!nonce || !opener) return Promise.resolve(null);
    return new Promise(resolve => {
        let done = false;
        const finish = (value: string | null) => {
            if (done) return;
            done = true;
            window.removeEventListener('message', onReply);
            clearInterval(retry);
            clearTimeout(giveUp);
            resolve(value);
        };
        const onReply = (event: MessageEvent) => {
            if (event.origin !== window.location.origin || event.source !== opener) return;
            const data = event.data as { type?: string; nonce?: string; secret?: unknown } | null;
            if (!data || data.type !== SECRET || data.nonce !== nonce || typeof data.secret !== 'string') return;
            finish(data.secret);
        };
        const ask = () => {
            try { opener.postMessage({ type: READY, nonce }, window.location.origin); } catch { finish(null); }
        };
        window.addEventListener('message', onReply);
        // Re-ask a few times in case the opener's listener was still being set up.
        const retry = setInterval(ask, 500);
        const giveUp = setTimeout(() => finish(null), timeoutMs);
        ask();
    });
}

/**
 * Splits credential-bearing query params (the legacy ?password= and the one-time
 * ?handoff= nonce) off a location.search string, so the caller can replace the
 * history entry with `search` and never render or re-read them.
 */
export function splitSensitiveParams(search: string): { search: string; password: string; handoff: string; changed: boolean } {
    const params = new URLSearchParams(search);
    const password = params.get('password') || '';
    const handoff = params.get('handoff') || '';
    const changed = params.has('password') || params.has('handoff');
    params.delete('password');
    params.delete('handoff');
    const rest = params.toString();
    return { search: rest ? `?${rest}` : '', password, handoff, changed };
}
